import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NewsletterService } from '../services/newsletter.service';
import { AppState } from '../store';
import { Store } from '@ngrx/store';
import { NewsletterFormContent } from '../models/tenant.model';
import { selectNewsletterContent, selectTenantInfo } from '../store/selectors';
import { map } from 'rxjs/operators';
import { MessagesService } from '../shared/services/messages.service';
import { LoadingService } from '../shared/services/loading.service';
import { TrackingService } from '../services/tracking.service';
import { UrlBuilderService } from '../services/url-builder.service';
import { TranslateModule } from '@ngx-translate/core';
import { EMAIL_WITH_TLD_REGEX } from '../common/regex';
import { NewsletterSvgSpriteComponent } from './newsletter-svg-sprite.component';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'newsletter',
  templateUrl: './newsletter.component.html',
  styleUrls: ['./newsletter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, TranslateModule, NewsletterSvgSpriteComponent]
})
export class NewsletterComponent {
  private newsletterService = inject(NewsletterService);
  private loading = inject(LoadingService);
  private messages = inject(MessagesService);
  private store = inject<Store<AppState>>(Store);
  private tracking = inject(TrackingService);
  private ub = inject(UrlBuilderService);

  readonly mode = input<'popup' | 'home-page'>(null);
  readonly content = input<NewsletterFormContent>(undefined);
  readonly emailAdded = output();

  readonly form = inject(FormBuilder).group({
    email: ['', [Validators.required, Validators.pattern(EMAIL_WITH_TLD_REGEX)]],
    checkbox: [false, [Validators.requiredTrue]]
  });

  readonly newsletterUrl = this.ub.buildImageUrl('/images/newsletter/plane.png');

  private readonly tenantInfo = toSignal(this.store.select(selectTenantInfo));
  readonly brand = computed(() => this.tenantInfo()?.brandTheme);

  private readonly storeNewsletter = toSignal(
    this.store.select(selectNewsletterContent).pipe(
      map(c => c ?? ({} as NewsletterFormContent))
    )
  );
  readonly newsletter = computed(() => this.content() ?? this.storeNewsletter());
  readonly subscribed = signal(false);

  saveEmail() {
    if (this.subscribed()) return;

    if (!this.form.valid) {
      this.messages.i18nError('newsLetter.errorMessage');
      return;
    }

    const email = this.form.value.email;
    this.tracking.reportStudentLead();

    this.loading.showLoaderUntilCompleted(this.newsletterService.addToNewsletter(email)).subscribe({
      next: (res) => {
        if (res?.alreadySubscribed) {
          this.messages.i18nError('newsLetter.subscriptionError');
        } else {
          this.subscribed.set(true);
          this.messages.i18nInfo('newsLetter.thankYou');
          this.emailAdded.emit();
        }
      },
      error: err => {
        console.log('Error subscribing to newsletter', err);
        this.messages.error('Could not complete subscription. Please try again.');
      }
    });
  }

  toggle(checkbox: HTMLInputElement) {
    this.form.patchValue({ checkbox: !checkbox.checked });
  }
}
